<script setup>
import { computed, useSlots } from "vue";

import LearnMore from "./LearnMore.vue";
import LearnMoreItem from "./LearnMoreItem.vue";

const props = defineProps({
  title: { type: String, default: "" },
  learnMoreIds: { type: Array, default: () => [] },
  noBorder: { type: Boolean, default: false },
  headingLevel: { type: Number, default: 1, validator: (v) => [1, 2].includes(v) },
});

const slots = useSlots();
const hasLearnMore = computed(() => props.learnMoreIds.length > 0);
const hasActionsSlot = computed(() => !!slots.actions);
// Demote to <h2> when nested under a WizardHeader (which owns the page <h1>).
const titleTag = computed(() => ({ 1: "h1", 2: "h2" })[props.headingLevel] || "h1");
</script>

<template>
  <div class="content-header" :class="{ 'no-border': noBorder }">
    <div class="content-header-row">
      <component :is="titleTag" v-if="title" class="content-header-title">{{ title }}</component>
      <slot name="title-actions" />
      <div v-if="hasLearnMore || hasActionsSlot" class="content-header-actions">
        <LearnMore v-if="hasLearnMore">
          <LearnMoreItem v-for="id in learnMoreIds" :id :key="id" />
        </LearnMore>
        <span v-if="hasLearnMore && hasActionsSlot" class="action-divider" aria-hidden="true" />
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/layout";
@use "@/styles/mixins/typography";

.content-header {
  position: sticky;
  top: 0;
  z-index: 3;
  padding: 12px layout.$content-pad;
  background-color: c.$background-ui;
  border-bottom: 1px solid c.$border-light;
}

.content-header.no-border {
  border-bottom: none;
}

.content-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
}

.content-header-title {
  @include typography.large("bold");

  margin: 0;
  color: c.$main;
}

.content-header-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-divider {
  width: 1px;
  height: 16px;
  background-color: c.$border;
  flex-shrink: 0;
}
</style>
